"use client"

import { useMutation, type UseMutationOptions } from "@tanstack/react-query"
import { Result } from "@praha/byethrow"
import { toast } from "sonner"
import { resultErrorMessage } from "./useAppQuery"

type AppMutationOptions<TData, TVariables, TContext> = Omit<
  UseMutationOptions<Result.Result<TData, string>, unknown, TVariables, TContext>,
  "onSuccess"
> & {
  errorMessage: string
  onSuccess?: (value: TData) => void
}

export function useAppMutation<TData = unknown, TVariables = void, TContext = unknown>(
  options: AppMutationOptions<TData, TVariables, TContext>,
) {
  const {
    errorMessage,
    onSuccess,
    ...rest
  } = options

  const onFail = (result: Result.Result<never, Error | string>) => {
    const fullErrorMessage = resultErrorMessage( errorMessage, result)
    if (fullErrorMessage) {
      toast.error(fullErrorMessage)
    }
  }

  return useMutation<Result.Result<TData, string>, unknown, TVariables, TContext>({
    ...rest,
    onSuccess: (data) => {
      if (Result.isFailure(data)) {
        onFail(data)
      } else
        onSuccess?.(data.value)
    },
    onError: (error) => {
      onFail(Result.fail(error instanceof Error ? error : new Error(String(error))))
    },
  })
}
